/**
 * Benchmark cells → chart series (HOF-19 Phase B).
 *
 * The server returns the benchmarks in their own array (`benchmark_cells`),
 * read from their own fact type, grouped by `(catalog_item_id, series_id)` —
 * no side, no counterparty, no channel, because a price in somebody else's
 * shop or on somebody else's market has none of those.
 *
 * Two lines arrive in that one list, told apart by `series_id` and named for
 * the MARKET LEVEL they measure — `…__benchmark__consumer` (what the end
 * customer pays) and `…__benchmark__trade` (what the Großmarkt pays). The
 * farmer sells at both levels and needs a benchmark at each.
 *
 * **Why this is a separate module and a separate map.** `cells.ts` folds the
 * booked and estimate slices into ONE list per item, each series carrying an
 * `estimate` stamp, because everything computing a statistic cuts on
 * `!s.estimate` first. That is an opt-OUT: a consumer that forgets the cut
 * averages a reference price into a price a buyer paid.
 *
 * AC 8 does not tolerate an opt-out. The benchmark must never reach the
 * mengengewichteter Durchschnitt, a tile Ø, a day panel or a candle — so it is
 * not in that map at all. A consumer that does not know about benchmarks cannot
 * fold one in by forgetting anything, which is the same argument the server
 * makes by publishing under a fact type none of the own-price readers name.
 *
 * **The value is the plain mean.** A shelf price has no traded quantity behind
 * it, so the import writes no weight and the server asks for none; the cell's
 * `volume_weighted_mean` is null by construction and `weighted` is false. Using
 * `mean` here is not a fallback — it is the only honest statistic this
 * population has.
 */
import type { PriceCell } from '../../../services/twinService';
import type { PriceUnit, Quality } from './catalog';
import type { ObservedPoint, PriceSeries } from './types';

/** The unit every benchmark observation is published in. */
export const BENCHMARK_UNIT: PriceUnit = '€/kg';

/**
 * The market levels the chart draws, and their names.
 *
 * **A line is a level, never a source (Antonio, 2026-09-01).** The chart used
 * to name its reference line after whoever supplied it — `AMI MarktMonitor` —
 * which was accurate while exactly one tenant had exactly one panel and wrong
 * in every other case: a tenant with no licence read a claim about a source it
 * does not have, and once the open collectors landed, a farmer got two lines
 * that mean almost the same thing. So the consumer line pools every
 * consumer-level population the tenant is entitled to (the open collectors,
 * plus its own licensed panel where it has one), carries one name, and answers
 * one question. `AMI MarktMonitor` survives only in the drill-down's per-row
 * `source` column, where it is provenance rather than a line name.
 *
 * `einzelhandel` is deliberately not a word here. The farm's own channel of
 * that name means selling TO retail (a trade-level price); this line is made
 * OF retail shelf prices. Same word, opposite ends of the chain — a farmer
 * reading "Einzelhandel 5,59" beside a line called "Handelspreise 9,41"
 * concludes his buyer underpays him by 40 %.
 */
export const BENCHMARK_LEVEL_SHORT = {
  /** What the end customer pays — the yardstick for Direktvermarktung. */
  consumer: 'Verbraucherebene',
  /**
   * What the Großmarkt pays — the yardstick for Großhandel/Einzelhandel.
   *
   * `Handelsebene`, never `Großhandel`: the farm's OWN channel is called
   * `grosshandel`, and a line named for a channel the reader also sells through
   * invites him to read the benchmark as his own realised price. Same trap as
   * `einzelhandel` below, one word further along.
   */
  trade: 'Handelsebene',
} as const;

export type BenchmarkLevel = keyof typeof BENCHMARK_LEVEL_SHORT;

/**
 * The drawn name: the word `Benchmark`, then the level (Antonio, 2026-09-02).
 *
 * `Verbraucherpreise` and `Großmarkt deutsche Ware` each named their level
 * accurately and neither said it was a REFERENCE. On a chart whose other lines
 * are the farm's own realised prices, a name that does not mark itself as a
 * benchmark is read as one more of the farm's own — so the prefix is doing the
 * work, and the level noun behind it says which end of the chain.
 *
 * What the trade name gives up is `deutsche Ware`, which is load-bearing: the
 * line is BLE notations for German origin only, and March 2025 measured German
 * Erdbeeren at 8,74 €/kg against 5,86 for the same week blended with Spanish
 * and Greek imports. It is not dropped, it MOVES — to the caveat under the plot
 * and to the ⓘ chip, both of which name it in a full sentence with room to say
 * why. A legend chip is a name, not a specification.
 */
export const BENCHMARK_LEVEL_LABEL = {
  consumer: `Benchmark ${BENCHMARK_LEVEL_SHORT.consumer}`,
  trade: `Benchmark ${BENCHMARK_LEVEL_SHORT.trade}`,
} as const satisfies Record<BenchmarkLevel, string>;

/** The drawn levels, in chain order (consumer end first). */
export const BENCHMARK_LEVELS = Object.keys(BENCHMARK_LEVEL_SHORT) as readonly BenchmarkLevel[];

/**
 * WHICH statistic a level's points are — the two are not computed the same way
 * and the reader can see the difference.
 *
 * `index` is the chained matched-model index (a shop panel: dozens of cells,
 * each reporting every week). `mean` is the observation-weighted average of
 * what the markets quoted (BLE: five Großmärkte). The server's
 * `benchmark_index.INDEX_METHOD` is the source of truth and carries the
 * measurements; this mirrors it for one purpose — **only a mean may wear an
 * `Ø`**. The tile that headed a chained level `Ø 2,45 €/kg` over sixteen rows
 * averaging 6,34 was promising an average it was never computing.
 *
 * Since HOF-250 this is the **fallback**, not the decision: the chain re-anchors
 * on a week's own weighted mean wherever that week forms no relatives, so a
 * consumer line carries both statistics and each point states its own
 * ({@link ObservedPoint.statistic}). Read the point first and this map only
 * where the point is silent — a server that predates the field, or a bucket
 * whose points disagree.
 */
export type BenchmarkStatistic = 'index' | 'mean';

export const BENCHMARK_LEVEL_STATISTIC: Record<BenchmarkLevel, BenchmarkStatistic> = {
  consumer: 'index',
  trade: 'mean',
};

/**
 * What to say where a level has no line for this product.
 *
 * Its own sentence per level rather than a template over the label: coverage is
 * asymmetric and always will be, so this text is read often, and
 * `Kein ${label}-Index` over the trade label produced "Kein Großmarkt deutsche
 * Ware-Index für dieses Produkt." A sentence a reader stumbles over is one they
 * stop trusting.
 */
export const BENCHMARK_LEVEL_ABSENT: Record<BenchmarkLevel, string> = {
  consumer: 'Keine Verbraucherpreise für dieses Produkt.',
  trade: 'Keine Großmarktnotierungen für deutsche Ware bei diesem Produkt.',
};

/**
 * The level a benchmark series id names, or `null` if it is not one.
 *
 * Anchored, so a farm's own series can never be mistaken for a benchmark and
 * an unknown level (a server drawing a line this build does not know about) is
 * honestly `null` rather than mislabelled.
 */
export function benchmarkLevel(seriesId: string): BenchmarkLevel | null {
  const match = /__benchmark__(consumer|trade)(?:__(?:bio|konventionell))?$/.exec(seriesId);
  return match ? (match[1] as BenchmarkLevel) : null;
}

/** Whether a series id is a benchmark line at all. */
export function isBenchmarkSeries(seriesId: string): boolean {
  return benchmarkLevel(seriesId) !== null;
}

/**
 * The legend name for a benchmark series — the market level, plus the Bio
 * split where the id carries one.
 *
 * Two levels must never share a name: they are prices at opposite ends of the
 * chain, and a reader comparing his own channel against the wrong one draws
 * exactly the wrong conclusion about what he is paid.
 */
export function benchmarkSourceLabel(seriesId: string): string {
  const level = benchmarkLevel(seriesId);
  const base = level ? BENCHMARK_LEVEL_LABEL[level] : 'Referenzpreise';
  return seriesId.endsWith('__bio') ? `${base} (Bio)` : base;
}

/**
 * The name to put in a sentence ABOUT a set of benchmark series — the caveat,
 * the axis refusal, the failure notes.
 *
 * The quality suffix is deliberately dropped: these sentences speak about the
 * population, and the Bio split is a legend concern. `null` for an empty set,
 * so a caller cannot render a sentence about nothing.
 *
 * The `Benchmark` prefix is said ONCE and the levels share it — "Benchmark
 * Verbraucher- und Handelsebene" reads as one thing named twice over, which is
 * what it is. Joining the full labels gave "Benchmark Verbraucherebene &
 * Benchmark Handelsebene" inside sentences that already had work to do.
 */
export function benchmarkPopulationLabel(seriesIds: readonly string[]): string | null {
  const present = BENCHMARK_LEVELS.filter((level) => seriesIds.some((id) => benchmarkLevel(id) === level));
  if (present.length === 0) return null;
  return `Benchmark ${present.map((level) => BENCHMARK_LEVEL_SHORT[level]).join(' & ')}`;
}

/**
 * The scope line on an own-empty tile whose item nonetheless holds a benchmark
 * (HOF-19 AC 22) — short, because it stands where the price would.
 *
 * One level names itself; two collapse to the generic word rather than being
 * concatenated, because a tile has room for a label, not a sentence.
 */
export function benchmarkTileLabel(seriesIds: readonly string[]): string | null {
  return benchmarkTileLabelFromLevels(
    BENCHMARK_LEVELS.filter((level) => seriesIds.some((id) => benchmarkLevel(id) === level)),
  );
}

/**
 * The same label, from the MATERIALISED presence bit (HOF-377 AC 18).
 *
 * The grid used exactly one bit out of the whole benchmark gather — presence —
 * and paid 5-8 UDB aggregates of 2-5 s each for it on every visit. The bit is now
 * a `benchmark_levels` list on the `preisspiegel_tile` summary, so the tile's own
 * label no longer needs a single benchmark series to have been fetched.
 *
 * Deliberately the same body as {@link benchmarkTileLabel} rather than a second
 * copy of the rule: the two must return the same string for every level set, or a
 * tenant on the summary path and a tenant on the fallback path would read
 * different words for the same data. `benchmarkTileLabel` now derives the levels
 * and delegates, so there is one rule with two entry points, and
 * `publicBenchmark.test.ts` asserts the agreement across every set.
 *
 * Unknown levels are ignored rather than guessed at: the drawn vocabulary is
 * `consumer`/`trade`, and a level the chart cannot draw must not put a label over
 * a line no surface can show.
 */
export function benchmarkTileLabelFromLevels(levels: readonly string[]): string | null {
  const present = BENCHMARK_LEVELS.filter((level) => levels.includes(level));
  if (present.length === 0) return null;
  if (present.length > 1) return 'Referenzpreise';
  return BENCHMARK_LEVEL_LABEL[present[0]];
}

/** A company form in a parenthetical: the operating entity, never a street. */
const LEGAL_FORM = /\b(gmbh|mbh|ohg|kgaa|kg|ag|se|e\.\s?k\.|e\.\s?g\.)\b/i;

/** A German postcode. Identifying to a postman, noise to a price reader. */
const POSTCODE = /\b\d{5}\b/g;

/**
 * A shop's name, trimmed to what tells one shop from another.
 *
 * Collectors disagree about what belongs in `retailer`, and the drill-down puts
 * them side by side in one list. Kaufland writes
 * `kaufland de3330 (berlin storkower str.)`; REWE writes
 * `rewe markt 1931630 (rewe markt gmbh, heidestr. 41, 10557 berlin / mitte)` —
 * the operating company (which only repeats the shop), the street, the postcode
 * AND the district, in a row that already prints the city one column over. The
 * result was one list at two very different densities, with the long rows
 * pushing the price the reader is scanning for off to the far side.
 *
 * So: drop the operating company, drop the postcode, and drop the city the row
 * states anyway — keeping whatever that city was qualifying, because
 * `heidestr. 41` and `mitte` are exactly what tells two REWE stores apart. What
 * survives is the shop id plus its human location hint, at the length the
 * Kaufland rows already read at.
 *
 * Lossless where it matters: the caller keeps the full string on the row's
 * `title`, so the address is one hover away and nothing is destroyed.
 */
export function compactRetailerLabel(
  retailer: string | null | undefined,
  city: string | null | undefined,
): string | null | undefined {
  if (!retailer) return retailer;
  const open = retailer.indexOf('(');
  const close = retailer.lastIndexOf(')');
  // No parenthetical, or a malformed one: nothing to reason about. Returned
  // verbatim rather than repaired — a name is data, not a format.
  if (open === -1 || close < open) return retailer;

  const head = retailer.slice(0, open).trim();
  const inner = retailer.slice(open + 1, close);
  const cityToken = (city ?? '').trim().toLowerCase();

  const kept: string[] = [];
  for (const raw of inner.split(',')) {
    let seg = raw.replace(POSTCODE, ' ').replace(/\s+/g, ' ').trim();
    if (!seg) continue;
    if (LEGAL_FORM.test(seg)) continue;
    if (cityToken) {
      const lower = seg.toLowerCase();
      if (lower === cityToken) continue;
      // `berlin / mitte` → `mitte`, `frankfurt/sachsenhausen` → `sachsenhausen`:
      // the city is already on the row, the district is not.
      //
      // Only where a DELIMITER follows the city, though. A space does not mark
      // a subdivision — it is just the next word, and cutting at it turns
      // `frankfurt am main` into `am main` (the city's own name, mangled) and
      // `berlin storkower str.` into a street with no city, neither of which is
      // shorter in any way that helps. A delimiter is the collector saying
      // "place, then place-within-place".
      const delimited = new RegExp(`^${cityToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[/·|-]\\s*`, 'i');
      const rest = seg.replace(delimited, '').trim();
      if (rest && rest !== seg) seg = rest;
    }
    kept.push(seg);
  }

  const tail = kept.join(', ');
  // A head-less name (the whole thing was a parenthetical) keeps its original
  // form: stripping it would leave an empty label, which names no shop at all.
  if (!head) return retailer;
  return tail ? `${head} (${tail})` : head;
}

interface ParsedBenchmarkCell {
  itemId: string;
  seriesId: string;
  date: string;
  value: number;
  n: number;
  statistic?: BenchmarkStatistic;
  chainBreak?: boolean;
}

/**
 * The wire's statistic name → the one thing the UI decides with it.
 *
 * A closed map rather than a cast: an unknown name (a newer server, a typo)
 * falls through to `undefined`, and the reader then degrades to the LEVEL's
 * declared method — which is what every consumer did before HOF-250. Mapping
 * an unrecognised name onto `'mean'` would print an `Ø` over an arithmetic
 * nobody performed, which is the one failure this whole field exists to stop.
 */
const WIRE_STATISTIC: Record<string, BenchmarkStatistic> = {
  chained_index: 'index',
  weighted_mean: 'mean',
  // HOF-544 — a trade week drawn from BOTH populations: the BLE mean and the
  // Shared Buy Index, folded at fixed weights. Not the average of any rows the
  // drill-down lists, so it reads as an index — no `Ø` — like a chained level.
  composite: 'index',
};

/**
 * The quality a benchmark series' id encodes (`…__ami__bio`), or undefined.
 *
 * Stamped onto the series (codex r2): the chart applies its quality selection
 * to benchmark series exactly as to own ones, and a series that carries none
 * would vanish the moment any quality is selected — the AMI line silently
 * gone, with the selector looking like the cause and nothing saying so.
 */
function qualityOf(seriesId: string): Quality | undefined {
  if (seriesId.endsWith('__bio')) return 'bio';
  if (seriesId.endsWith('__konventionell')) return 'konventionell';
  return undefined;
}

function parse(cell: PriceCell): ParsedBenchmarkCell | null {
  const keys = (cell.keys ?? {}) as Record<string, unknown>;
  const itemId = typeof keys.catalog_item_id === 'string' ? keys.catalog_item_id : null;
  const seriesId = typeof keys.series_id === 'string' ? keys.series_id : null;
  const date = typeof cell.bucket === 'string' ? cell.bucket : null;
  // `mean`, never `volume_weighted_mean` — see the module header. A cell whose
  // mean is not a finite number is dropped rather than charted as 0, which would
  // be a price nobody observed.
  const value = typeof cell.mean === 'number' && Number.isFinite(cell.mean) ? cell.mean : null;
  if (!itemId || !seriesId || !date || value === null) return null;
  const statistic = typeof cell.statistic === 'string' ? WIRE_STATISTIC[cell.statistic] : undefined;
  return {
    itemId,
    seriesId,
    date,
    value,
    n: typeof cell.n === 'number' ? cell.n : 0,
    ...(statistic ? { statistic } : {}),
    ...(cell.chain_break === true ? { chainBreak: true } : {}),
  };
}

/**
 * Benchmark series keyed by catalog item id.
 *
 * One series per `series_id`. Since HOF-214 the server sends ONE merged,
 * quality-less id per (item, level) (`erdbeeren__benchmark__consumer`): the
 * matched-model index chains price CHANGES within cells whose key includes
 * `organic`, so bio and konventionell never form a relative against each other
 * and pooling them cannot lift the line by the organic premium. The
 * `__bio`/`__konventionell` suffix parsing stays for the drill-down ids and
 * for deploy skew — a split series still charts correctly through this path.
 */
export function buildBenchmarkSeriesByItem(cells: PriceCell[]): Record<string, PriceSeries[]> {
  const bySeries = new Map<string, PriceSeries>();
  const points = new Map<string, Map<string, ObservedPoint>>();

  for (const cell of cells) {
    const parsed = parse(cell);
    if (!parsed) continue;
    if (!bySeries.has(parsed.seriesId)) {
      const quality = qualityOf(parsed.seriesId);
      bySeries.set(parsed.seriesId, {
        seriesId: parsed.seriesId,
        itemId: parsed.itemId,
        ...(quality ? { quality } : {}),
        // The panel is a market observation, not a trade the farm was party to.
        // `side` is required by the shared shape, and `verkauf` is the honest
        // reading of a SELLING price observed in a shop -- it is what a buyer
        // paid, seen from the counter rather than from the field.
        side: 'verkauf',
        counterparty: benchmarkSourceLabel(parsed.seriesId),
        unit: BENCHMARK_UNIT,
        points: [],
        layer: 'benchmark',
      });
      points.set(parsed.seriesId, new Map());
    }
    // One point per (series, day). A duplicate bucket would mean the server
    // grouped differently than declared; last wins rather than summing, because
    // summing two means is not a mean of anything.
    // The cell's `n` rides along in `lineCount`, the existing carrier for "how
    // many source rows this point stands for". For a level whose points are a
    // MEAN it is what lets a coarser display grain re-bucket to the same number
    // the drill-down lists (see `benchmarkChartLayer.bucketsFor`). Omitted where
    // the cell states none, which the re-bucket reads as one.
    // `statistic` rides along the same way and for the same reason: WHICH
    // statistic this point is decides whether it may wear an `Ø`, and since
    // HOF-250 that varies point to point along one consumer line.
    points.get(parsed.seriesId)?.set(parsed.date, {
      date: parsed.date,
      value: parsed.value,
      ...(typeof cell.n === 'number' && Number.isFinite(cell.n) ? { lineCount: cell.n } : {}),
      ...(parsed.statistic ? { statistic: parsed.statistic } : {}),
      // And so does the scale break, for the same reason one step further out:
      // the chart may not draw a line ACROSS a re-anchor.
      ...(parsed.chainBreak ? { chainBreak: true } : {}),
    });
  }

  const byItem: Record<string, PriceSeries[]> = {};
  for (const [seriesId, series] of bySeries) {
    series.points = [...(points.get(seriesId)?.values() ?? [])].sort((a, b) => a.date.localeCompare(b.date));
    (byItem[series.itemId] ??= []).push(series);
  }
  return byItem;
}

/**
 * Whether the benchmark may be drawn on the same axis as an item's own prices.
 *
 * AC 9. AMI is €/kg by construction; a great many catalog items are not (grain
 * is €/t, Kartoffeln €/dt — a factor of 10 and 1000). Two lines on one Y-axis in
 * different units is #2944's substitution bug with both numbers looking
 * plausible, so the answer is to REFUSE, never to convert: converting would put
 * a derived number on a chart whose whole argument is that it shows observations.
 *
 * `undefined` own unit means "not stated" — an absence, not a disagreement — and
 * is treated as co-plottable, matching how the own-price label already handles it.
 */
export function benchmarkIsCoPlottable(ownUnit: PriceUnit | undefined): boolean {
  return ownUnit === undefined || ownUnit === BENCHMARK_UNIT;
}
